import { useEffect, useRef, useState } from "react";
import { Icon } from "../../components/Icon";
import { FilePreview } from "./FilePreview";
import styles from "./FileExplorerWithPreview.module.css";
import type { FileExplorerWithPreviewProps } from "./types";

export function FileExplorerWithPreview({
  contextText,
  emptyMessage = "No files found.",
  filterGroups = [],
  filterLabel,
  filterOptions = [],
  filterValue,
  items,
  label = "Files",
  loading = false,
  loadingMore = false,
  mode = "compact",
  multiSelect = false,
  onEndReached,
  onFilterChange,
  onQueryChange,
  onSelectionChange,
  query,
  selectedIds = [],
  showControls = true,
  showHeader = true,
  visibleRows = 5,
}: FileExplorerWithPreviewProps) {
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef<HTMLDivElement | null>(null);
  const groupedFilterOptions = filterGroups.flatMap((group) => group.options);
  const resolvedFilterOptions = groupedFilterOptions.length
    ? groupedFilterOptions
    : filterOptions;
  const selectedFilter = resolvedFilterOptions.find(
    (option) => option.value === filterValue,
  );
  const resolvedFilterLabel =
    selectedFilter?.label ?? filterLabel ?? filterOptions[0]?.label ?? "Filter";
  const handleBodyScroll = (event: React.UIEvent<HTMLDivElement>) => {
    if (!onEndReached || loading || loadingMore) {
      return;
    }

    const element = event.currentTarget;
    const distanceToBottom =
      element.scrollHeight - element.scrollTop - element.clientHeight;
    if (distanceToBottom <= 80) {
      onEndReached();
    }
  };

  useEffect(() => {
    if (!isFilterOpen) {
      return;
    }

    const onPointerDown = (event: PointerEvent) => {
      if (!filterRef.current?.contains(event.target as Node)) {
        setIsFilterOpen(false);
      }
    };

    window.addEventListener("pointerdown", onPointerDown);
    return () => window.removeEventListener("pointerdown", onPointerDown);
  }, [isFilterOpen]);

  const selectItem = (itemId: string, event: React.MouseEvent) => {
    if (items.find((item) => item.id === itemId)?.disabled) {
      return;
    }

    const isModifierSelection = event.ctrlKey || event.metaKey;
    const isSelected = selectedIds.includes(itemId);

    if (isModifierSelection && isSelected) {
      onSelectionChange(
        selectedIds.filter((selectedId) => selectedId !== itemId),
      );
      return;
    }

    if (multiSelect && isModifierSelection) {
      onSelectionChange([...selectedIds, itemId]);
      return;
    }

    onSelectionChange(isSelected && multiSelect ? [] : [itemId]);
  };

  return (
    <section className={styles.fileExplorerWithPreview}>
      {showHeader ? (
        <div className={styles.header}>
          <span className={styles.label}>{label}</span>
        </div>
      ) : null}
      {showControls ? (
        <div className={styles.controls}>
          <label className={styles.searchInput}>
            <Icon className={styles.searchIcon} name="search" size="s" />
            <input
              className={styles.searchField}
              type="search"
              value={query}
              placeholder="Search"
              onChange={(event) => onQueryChange(event.target.value)}
            />
            {query ? (
              <button
                className={styles.clearSearchButton}
                type="button"
                aria-label="Clear search"
                onClick={() => onQueryChange("")}
              >
                <Icon name="x" size="m" />
              </button>
            ) : null}
          </label>
          {filterOptions.length || filterGroups.length ? (
            <div ref={filterRef} className={styles.filterWrap}>
              <button
                className={styles.filterInput}
                type="button"
                onClick={() => setIsFilterOpen((open) => !open)}
              >
                <Icon className={styles.filterIcon} name="filter" size="s" />
                <span className={styles.filterText}>{resolvedFilterLabel}</span>
                <Icon className={styles.filterArrow} name="down" size="s" />
              </button>
              {isFilterOpen ? (
                filterGroups.length ? (
                  <div className={styles.groupedMenu} role="menu">
                    {filterGroups.map((group) => (
                      <div
                        key={group.key ?? group.label}
                        className={styles.groupedMenuSection}
                      >
                        <div className={styles.groupedMenuTitle}>
                          {group.label}
                        </div>
                        {group.options.map((option) => (
                          <button
                            key={option.value}
                            className={[
                              styles.groupedMenuOption,
                              option.value === filterValue
                                ? styles.groupedMenuOptionActive
                                : "",
                            ].join(" ")}
                            type="button"
                            role="menuitemradio"
                            aria-checked={option.value === filterValue}
                            onClick={() => {
                              onFilterChange?.(option.value);
                              setIsFilterOpen(false);
                            }}
                          >
                            {option.label}
                          </button>
                        ))}
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className={styles.filterMenu} role="menu">
                    {filterOptions.map((option) => (
                      <button
                        key={option.value}
                        className={[
                          styles.filterMenuItem,
                          option.value === filterValue
                            ? styles.filterMenuItemSelected
                            : "",
                        ].join(" ")}
                        type="button"
                        role="menuitemradio"
                        aria-checked={option.value === filterValue}
                        onClick={() => {
                          onFilterChange?.(option.value);
                          setIsFilterOpen(false);
                        }}
                      >
                        {option.label}
                      </button>
                    ))}
                  </div>
                )
              ) : null}
            </div>
          ) : null}
        </div>
      ) : null}
      <div
        className={[
          styles.body,
          mode === "full" ? styles.bodyFull : "",
          visibleRows === 3 ? styles.bodyThreeRows : styles.bodyFiveRows,
        ].join(" ")}
        onScroll={handleBodyScroll}
      >
        {loading ? (
          <div className={styles.skeletonStack} aria-label="Loading files">
            {Array.from({ length: visibleRows }, (_, index) => (
              <div
                key={`file-explorer-skeleton-${index}`}
                className={[
                  styles.skeletonItem,
                  mode === "full" ? styles.skeletonItemFull : "",
                ].join(" ")}
              >
                <span className={styles.skeletonPreview} />
                <span className={styles.skeletonText} />
              </div>
            ))}
          </div>
        ) : items.length ? (
          <>
            {items.map((item) => {
              const selected = selectedIds.includes(item.id);
              return (
                <button
                  key={item.id}
                  className={[
                    styles.filePreview,
                    mode === "full" ? styles.slidePreview : "",
                    selected ? styles.selected : "",
                    item.disabled ? styles.filePreviewDisabled : "",
                  ].join(" ")}
                  disabled={item.disabled}
                  draggable={false}
                  title={item.disabledReason}
                  type="button"
                  onDragStart={(event) => event.preventDefault()}
                  onClick={(event) => selectItem(item.id, event)}
                >
                  <FilePreview item={item} mode={mode} selected={selected} />
                </button>
              );
            })}
            {loadingMore ? (
              <div
                className={[
                  styles.skeletonItem,
                  mode === "full" ? styles.skeletonItemFull : "",
                ].join(" ")}
                aria-label="Loading more"
              >
                <span className={styles.skeletonPreview} />
                <span className={styles.skeletonText} />
              </div>
            ) : null}
          </>
        ) : (
          <div className={styles.empty}>{emptyMessage}</div>
        )}
      </div>
      {contextText ? (
        <span className={styles.context}>{contextText}</span>
      ) : null}
    </section>
  );
}
